const en = {
  common: {
    save: "Save",
    cancel: "Cancel",
    delete: "Delete",
    edit: "Edit",
    back: "Back",
    close: "Close",
    loading: "Loading…",
    saving: "Saving…",
    add: "Add",
    update: "Update",
    confirm: "Confirm",
    all: "All",
    perMonth: "/mo",
    perMonthLabel: "Per month",
    perYearLabel: "Per year",
    somethingWrong: "Something went wrong",
    signOut: "Sign out",
    signedOut: "You have been signed out",
    previous: "Previous",
    next: "Next",
    nav: {
      home: "Home",
      subscriptions: "Subs",
      add: "Add",
      salary: "Income",
      income: "Income",
      profile: "Profile",
    },
    language: "Language",
    english: "English",
    norwegian: "Norwegian (Bokmål)",
  },
  landing: {
    eyebrow: "Personal finance",
    headline: "See your financial reality.",
    body: "VISIKT brings your recurring costs, salary growth, and real purchasing power together. No budgeting. No nagging. Just clarity.",
    getStarted: "Get started",
    haveAccount: "I already have an account",
    pillars: "TWO PILLARS",
    pillar1Title: "Recurring costs",
    pillar1Body:
      "Full view of everything that hits your account every month — streaming, software, telecom, fitness. Categorised, normalised, visible.",
    pillar2Title: "Salary & purchasing power",
    pillar2Body:
      "Compare salary growth against Norwegian CPI inflation. See your real wage growth, year over year.",
    footerYear: "VISIKT / 2026",
    footerScope: "NORWAY-FIRST",
    login: "Sign in",
    pricing: "Pricing",
  },
  footer: {
    faq: "FAQ",
    partOf: "part of",
    company: "VIKEEP AS",
    about: "About",
    privacy: "Privacy",
  },
  auth: {
    welcomeBack: "Welcome back",
    newAccount: "New account",
    forgot: "Reset password",
    signInHeadline: "Sign in.",
    signUpHeadline: "Create account.",
    forgotHeadline: "Forgot password.",
    subtitle: "See your financial reality — without the noise.",
    forgotSubtitle: "We'll email you a link to set a new password.",
    continueGoogle: "Continue with Google",
    continueApple: "Continue with Apple",
    appleFailed: "Apple sign-in failed",
    or: "or",
    name: "Name",
    email: "Email",
    password: "Password",
    passwordHint: "At least 8 characters",
    submitSignIn: "Sign in",
    submitSignUp: "Create account",
    submitForgot: "Send link",
    waiting: "Wait…",
    forgotLink: "Forgot password?",
    noAccount: "No account?",
    createOne: "Create one",
    haveAccount: "Already have an account?",
    backToSignIn: "Back to sign in",
    googleFailed: "Could not sign in with Google",
    invalidEmail: "Invalid email address",
    passwordMin: "At least 8 characters",
    accountCreated: "Account created. You are signed in.",
    resetSent: "Check your email for a password reset link.",
    passwordUpdated: "Password updated.",
    newPasswordHeadline: "Set a new password.",
    newPasswordBody: "Choose a new password with at least 8 characters.",
    newPasswordPlaceholder: "New password",
    savePassword: "Save password",
  },
  register: {
    eyebrow: "Get started",
    headline: "Create your VISIKT account.",
    subtitle: "See your financial reality — without the noise.",
    firstName: "First name",
    lastName: "Last name",
    postalCode: "Postal code",
    city: "City",
    country: "Country",
    phone: "Phone number",
    occupation: "Occupation",
    confirmPassword: "Confirm password",
    submit: "Create account",
    checkEmail: "Check your email to confirm your account.",
    errors: {
      required: "Required",
      confirmMismatch: "Passwords do not match",
      phone: "Enter a valid phone number",
      postalNo: "Norwegian postal code must be 4 digits",
      signupFailed: "We couldn't create your account. Send us the details below and we'll sort it.",
      alreadyRegistered:
        "This email is already registered. Try signing in or resetting your password.",
      agreeRequired: "You must agree to the Terms and Privacy Policy to continue.",
    },
    agreeTos:
      "I agree to the <tosLink>Terms of Service</tosLink> and <privLink>Privacy Policy</privLink>.",
  },
  nudge: {
    subs: "You have {{count}} subscriptions. Free covers 3 — upgrade to Pro for unlimited.",
    salary:
      "You have {{count}} years of salary history. Free covers 3 — upgrade for the full timeline.",
    cta: "Upgrade",
  },
  occupation: {
    label: "Occupation",
    placeholder: "Select occupation",
    student: "Student",
    employee: "Employee",
    other: "Other",
  },
  gate: {
    moreCta: "+{{count}} more — upgrade to see all",
    moreSubsAria: "Upgrade to see {{count}} more subscriptions",
    moreSalaryAria: "Upgrade to see {{count}} more salary entries",
    moreYearsAria: "Upgrade to see {{count}} more years of history",
    statsNote: "Based on your top 3 · upgrade for full totals",
    upgrade: "Upgrade to Pro",
    cpiTitle: "See your full inflation history",
    cpiBody:
      "Free shows this year only. Upgrade to compare every year of your salary against Norwegian CPI.",
    statsLockedTitle: "Full stats explorer",
    statsLockedBody:
      "Cumulative growth, CAGR, and multi-year real-value figures are available on Pro.",
  },
  contact: {
    eyebrow: "Support",
    headline: "We're here to help.",
    subtitle: "Tell us what happened and we'll get back to you quickly.",
    name: "Name",
    phone: "Phone",
    message: "Message",
    describe: "Describe the issue you're experiencing",
    describePlaceholder: "What went wrong, what were you trying to do…",
    submit: "Send message",
    autoIntro: "I ran into a problem: {{reason}}\n\nDetails:",
    resetSuggestion: "That email already has an account. Want a password reset link?",
    resetAction: "Send reset link",
    receivedEyebrow: "Received",
    receivedHeadline: "Thanks — we've got it.",
    receivedBody: "A human will follow up by email as soon as possible.",
    backHome: "Back to home",
    errors: {
      description: "Please write a message (at least 10 characters).",
      submitFailed: "Something went wrong sending your message. Please try again.",
    },
  },

  completeProfile: {
    eyebrow: "One more step",
    headline: "Complete your profile.",
    subtitle: "A few details we need before you continue.",
    submit: "Save and continue",
    saved: "Profile saved.",
    agreeTos:
      "I agree to the <tosLink>Terms of Service</tosLink> and <privLink>Privacy Policy</privLink>.",
    agreeRequired: "You must agree to the Terms and Privacy Policy to continue.",
  },
  plans: {
    eyebrow: "Choose your plan",
    headline: "Pick the plan that fits.",
    body: "Start free. Upgrade whenever you're ready — checkout opens shortly.",
    upgradeSoon: "Upgrade to Pro",
    checkoutSoon: "Checkout unavailable — try again shortly.",
    startProMonthly: "Start Pro — monthly",
    startProYearly: "Start Pro — yearly",
    keepPro: "Keep Pro (undo cancellation)",
    currentPlan: "Current plan",
    free: {
      continueFree: "Continue on Free",
      continue: "Continue",
    },
    renewalInfoMonthly:
      "{{price}} NOK/month, incl. VAT where applicable. Auto-renews monthly until you cancel. See the <tosLink>Terms of Service</tosLink>.",
    renewalInfoYearly:
      "{{price}} NOK/year, incl. VAT where applicable. Auto-renews yearly until you cancel. See the <tosLink>Terms of Service</tosLink>.",
    withdrawalAck:
      "I accept the <tosLink>Terms of Service</tosLink>, request that VISIKT Pro starts immediately, and understand that I lose my 14-day right of withdrawal once the service has been fully delivered.",
    withdrawalRequired:
      "Please accept the Terms and the immediate-start / withdrawal-right notice to continue.",
  },
  checkoutReturn: {
    finalizing: "Confirming your subscription…",
    confirmation: {
      eyebrow: "Order confirmation",
      headline: "You're all set — welcome to VISIKT Pro.",
      intro:
        "Please keep this confirmation for your records. It documents the contract you concluded with VIKEEP AS.",
      operator: "Operator",
      operatorValue: "VIKEEP AS · org.nr 938 008 272 · Oslo, Norway",
      contact: "Contact",
      plan: "Plan",
      planValue: "VISIKT Pro",
      cycleMonthly: "Billed monthly",
      cycleYearly: "Billed yearly",
      price: "Price",
      priceMonthly: "{{price}} NOK / month (incl. VAT where applicable)",
      priceYearly: "{{price}} NOK / year (incl. VAT where applicable)",
      renewal: "Renewal",
      renewalMonthly: "Auto-renews monthly at the then-current price until you cancel.",
      renewalYearly: "Auto-renews yearly at the then-current price until you cancel.",
      purchasedAt: "Purchased",
      withdrawalHeading: "Right of withdrawal (angrerett)",
      withdrawalAckLabel: "You accepted",
      withdrawalAckText:
        "I accepted the Terms of Service, requested that VISIKT Pro start immediately, and confirmed that I lose my 14-day right of withdrawal once the service has been fully delivered.",
      manage: "You can cancel or downgrade at any time from your Profile.",
      termsLinkLabel: "Terms of Service",
      download: "Download confirmation",
      continue: "Continue to dashboard",
      filename: "visikt-pro-confirmation",
    },
  },
  dashboard: {
    eyebrow: "Overview",
    adviceNotice:
      "Informational only — not financial advice. Figures are calculated from your inputs and official CPI data and may be revised.",
    hello: "Hello, {{name}}",
    fixedRing: "FIXED",
    fixedMonthly: "FIXED COSTS / MO",
    perYear: "{{value}} / year",
    activeOne: "active",
    activeMany: "active",
    emptySubs: "Add your first subscription →",
    purchasingPower: "PURCHASING POWER",
    realGrowth: "REAL GROWTH",
    emptySalary: "No salary data added yet",
    grossSalary: "Gross salary",
    cpiInflation: "Inflation",
    cpiProvisional: "Provisional (12-mo.)",
    yoy: "YoY",
    activeSubs: "ACTIVE SUBSCRIPTIONS",
    seeAll: "See all →",
    emptyActive: "No active subscriptions yet.",
    categories: "CATEGORIES",
    newSub: "New subscription",
    fixedRingHelp:
      "The share of your gross monthly salary that goes to fixed subscriptions right now. Calculated as active monthly subscriptions ÷ (latest gross annual salary ÷ 12).",
    ringOfSalary: "of monthly salary",
    ringNoSalaryLabel: "TOTAL / MO",
    ringNoSalaryHint: "Add your salary to see subscriptions as a share of income.",
    realGrowthHelp:
      "Your pay rise adjusted for inflation — whether your money actually bought more this year, even if the number on your payslip went up.",
    purchasingPowerHelp:
      "This section compares your income growth against inflation for the selected year, so you can see whether you gained or lost ground in real terms.",
    categoriesHelp:
      "How your monthly subscription spending breaks down by category, based on your active subscriptions.",
    cpiHelp:
      "How much prices in Norway have risen over the past year, measured by the consumer price index (CPI) from Statistics Norway.",
    nominalRaise: "PAY RISE {{year}}",
    realRaise: "REAL RAISE (AFTER INFLATION)",
    nominalVsRealTitle: "SALARY VS REAL VALUE",
    nominalVsRealHelp:
      "Your actual salary (solid) vs the same salary expressed in {{year}} kroner (dashed). The gap is purchasing power eroded by inflation.",
    legendNominal: "Salary",
    legendReal: "Real ({{year}} kr)",

    cumulativeAhead:
      "Since {{year}}, prices rose {{cpi}} and your salary rose {{sal}} — you're {{diff}} ahead in real terms.",
    cumulativeBehind:
      "Since {{year}}, prices rose {{cpi}} and your salary rose {{sal}} — you're {{diff}} behind in real terms.",
    subsShareLabel: "SUBS / SALARY",
    subsAnnualLabel: "PER YEAR",
    subsSavedLabel: "SAVED BY PAUSING",
    subsSavedSub: "{{count}} paused",
    percentOfSalary: "{{value}} of monthly salary",
  },
  subs: {
    eyebrow: "Subscriptions",
    one: "agreement",
    many: "agreements",
    loading: "Loading…",
    emptyFilter: "No agreements in this category.",
    pause: "Paused",
    newSub: "New subscription",
    deleteConfirm: "Delete {{name}}?",
    deleted: "Deleted",
    updated: "Updated",
    added: "Added",
    step: "Step {{n}} of 4",
    edit: "Edit",
    manualHeader: "Manual entry",
    chooseTitle: "New subscription",
    chooseQuestion: "How would you like to add this subscription?",
    selectService: "Select service",
    selectServiceBody: "Quickly find from catalog with country prices",
    recommended: "Recommended",
    manual: "Manual entry",
    manualBody: "Enter details yourself if the service isn't listed",
    pickService: "Pick service",
    pickPlan: "Pick plan",
    review: "Review",
    searchPlaceholder: "Search service or category…",
    noResults: "No matches.",
    country: "Country",
    fromCatalog: "From catalog — adjust if needed.",
    fields: {
      name: "Name",
      category: "Category",
      cycle: "Cycle",
      cost: "Cost",
      currency: "Currency",
      startDate: "Start date",
      active: "Active",
    },
    trackTitle: "Track a subscription",
    editTitle: "Edit subscription",
    enterManually: "Enter manually",
    useCatalog: "Use catalog",
    serviceSearchPlaceholder: "Search streaming, music, news…",
    plansCount: "{{n}} plans",
    plansCountOne: "1 plan",
    addSubscription: "Add subscription",
    saveChanges: "Save changes",
    section: {
      catalog: "Catalog",
      service: "Service",
      plan: "Plan",
      details: "Details",
    },
    save: "Save",
    validation: {
      nameRequired: "Name is required",
      costPositive: "Must be positive",
      currencyLen: "Currency must be 3 characters",
      statusRequired: "Status is required",
    },
    monthlySpend: "Monthly spend",
    activeYear: "{{count}} active · {{yearly}} / year",
    statMonthly: "Monthly",
    statAnnual: "Annual",
    statSavings: "Savings",
    runRate: "Run-rate",
    unused: "{{n}} unused",
    trendEmpty: "Trend appears once you have at least two months of tracked subscriptions.",
    listTitle: "Subscriptions",
    total: "{{n}} total",
    pageIndicator: "Page {{current}} of {{total}}",
    next: "Next",
    creepTitle: "SUBSCRIPTION CREEP",
    creepHelp:
      "How your monthly subscription total has grown as you've added services over time. Uses each subscription's start date.",
    creepAxisFrom: "{{value}}/mo in {{year}}",
    creepAxisNow: "{{value}}/mo now",
    breakdownTitle: "BY CATEGORY",
    breakdownHelp: "How your active monthly subscription cost is distributed across categories.",
    filterActive: "Active",
    filterPaused: "Paused",
    filterUnknown: "Unknown",
    filterAll: "All",
    sortLabel: "Sort",
    statusLabel: "Status",
    unknown: "Unknown",
    statusPlaceholder: "Select status",

    categoryLabel: "Category",
    categoryAll: "All categories",

    sortCostDesc: "Highest cost",
    sortCostAsc: "Lowest cost",
    sortNameAsc: "Name (A–Z)",
    sortDateDesc: "Newest first",
    filterSummary: "{{count}} · {{value}}/mo",
    percentOfSalary: "{{value}} of monthly salary",
    percentOfSalaryHelp:
      "Your total active monthly subscriptions divided by your most recent gross monthly salary (latest gross annual ÷ 12).",
    inBaseYearKroner: "in {{year}} kroner",
    realCostTitle: "REAL COST",
    realCostHelp:
      "Your current monthly subscription total deflated to the earliest tracked salary year using cumulative Norwegian CPI — what today's spend would have felt like back then.",
  },
  salary: {
    eyebrow: "Salary & purchasing power",
    none: "No salary registered",
    growth: "Pay rise",
    cpi: "Inflation",
    real: "Real change",
    realGrowth: "REAL WAGE GROWTH",
    history: "SALARY HISTORY",
    total: "{{n}} total",
    pageIndicator: "Page {{current}} of {{total}}",
    emptyHistory: "Add your first salary entry to see growth.",
    newSalary: "New salary",
    legendGrowth: "Pay rise",
    legendCpi: "Inflation",
    firstRunHint:
      "Add your annual salary for each year. VISIKT then calculates how much your purchasing power has actually changed, adjusted for inflation.",
    fixedSalary: "Fixed salary",
    bonusLabel: "Bonus",
    vacationPayLabel: "Vacation pay",
    otherIncomeLabel: "Other income",
    totalPaid: "Total paid",
    benefitsLabel: "Perks",
    benefitsNotPaidOut: "Perks",
    benefitsNotPaidOutHelp:
      "Things you receive from your employer that are not paid as money, such as a phone plan or insurance. Called naturalytelser on your payslip. Not included in total paid.",
    vacationReplacesSalary: "Vacation pay",
    vacationReplacesSalaryHelp:
      "Vacation pay is salary earned the previous year, paid out when you take holiday instead of your ordinary salary. It is therefore not additional to your annual salary, and is not included in total paid.",
    sinceYear: "since {{year}}",
    realChangeCaption: "real change · after inflation",
    nominalShort: "Pay rise",
    inflationShort: "Inflation",
    expandRow: "Show breakdown for {{year}}",
    collapseRow: "Hide breakdown for {{year}}",

    dialog: {
      titleNew: "New salary entry",
      titleEdit: "Edit entry",
      gross: "Gross annual salary (NOK)",
      effective: "Effective from",
      bonusOptional: "Bonus (optional)",
      extraTitle: "Additional compensation",
      vacationPay: "Vacation pay (feriepenger)",
      vacationPayHint:
        "Vacation pay is salary earned last year, paid out when you take holiday instead of your ordinary salary. It replaces salary rather than adding to it, so it is shown separately and not included in total paid.",
      benefits: "Perks",
      benefitsHint:
        "Non-cash things you receive from your employer such as a phone plan, insurance, or a company car. Called naturalytelser on your payslip.",

      otherIncome: "Other income",
      notes: "Notes",
      errInvalidNumber: "Enter a valid number.",
      errNegative: "Value must be zero or greater.",
      errTooLarge: "Value is too large.",
      errDateRequired: "Pick a date.",
      errFutureDate: "Date cannot be in the future.",
    },
    confirmDelete: "Delete this salary entry?",
    statsTitle: "STATS",
    statsMetric: "Metric",
    statsRange: "Range",
    rangeAll: "All",
    rangeLast3: "3y",
    rangeLast5: "5y",
    rangeLast10: "10y",
    rangeCustom: "Custom",
    statsFrom: "From",
    statsTo: "To",
    yearsSuffix_one: "{{count}} year",
    yearsSuffix_other: "{{count}} years",
    needTwoYears: "Need at least two years of data.",
    metrics: {
      totalEarned: "Total earned",
      totalEarnedHelp: "Sum of each year's gross annual salary in the range.",
      avgSalary: "Average salary",
      avgSalaryHelp: "Mean gross annual salary across the range.",
      cumGrowth: "Cumulative growth",
      cumGrowthHelp: "Salary change from first to last year in the range.",
      cagr: "Avg. annual growth",
      cagrHelp: "Compound annual growth rate (CAGR) of salary.",
      cumCpi: "Cumulative CPI",
      cumCpiHelp: "Compounded inflation across the range.",
      avgCpi: "Avg. annual CPI",
      avgCpiHelp: "Compound annual inflation rate.",
      cumReal: "Cumulative real",
      cumRealHelp: "Real (inflation-adjusted) salary change across the range.",
      avgReal: "Avg. annual real",
      avgRealHelp: "Compound annual real growth.",
      purchasingPower: "Purchasing power",
      purchasingPowerHelp: "Today's salary expressed in first-year NOK.",
    },
    realHelp:
      "Your pay rise adjusted for inflation. If your salary rises 4 % while prices rise 5 %, your purchasing power has fallen — even though you earn more.",
    cpiHelp:
      "How much prices in Norway have risen over the past year, measured by the consumer price index (CPI) from Statistics Norway.",
    growthHelp:
      "How much your salary has risen since the previous year, before inflation is taken into account.",
    cpiProvisionalHelp:
      "This year's official inflation figure isn't published yet (SSB releases it each January for the year before). This is a provisional estimate based on the latest available monthly data, and may change once the final figure is published.",

    statsMetricHelp_totalEarned: "The sum of your salary across every year in the selected range.",
    statsMetricHelp_avgSalary: "Your average yearly salary across the selected range.",
    statsMetricHelp_cumGrowth:
      "How much your salary grew in total from the first year to the last year in this range, in percent.",
    statsMetricHelp_cagr:
      "CAGR (Compound Annual Growth Rate) is your average yearly growth rate, smoothed out across the whole range — a fairer way to compare growth over different time periods than a simple average.",
    statsMetricHelp_cumCpi:
      "How much prices rose in total across the selected range, compounded year over year — not just added together.",
    statsMetricHelp_avgCpi: "The average yearly inflation rate across the selected range.",
    statsMetricHelp_cumReal:
      "Your total real (inflation-adjusted) growth across the whole range — the closest single number to 'did I actually get ahead?'",
    statsMetricHelp_avgReal: "Your average yearly real growth across the selected range.",
    statsMetricHelp_purchasingPower:
      "What your most recent salary is actually worth, expressed in the buying power of the first year in this range. Shows whether your money goes further or less far than it used to.",
  },
  profile: {
    eyebrow: "Profile",
    title: "Settings",
    email: "Email",
    name: "Name",
    namePlaceholder: "Your name",
    account: "ACCOUNT",
    currency: "Currency",
    updated: "Profile updated",
    personal: "PERSONAL DETAILS",
    firstName: "First name",
    lastName: "Last name",
    postalCode: "Postal code",
    city: "City",
    country: "Country",
    phone: "Phone number",
    occupation: "Occupation",
    plan: "PLAN",
    currentPlan: "Current plan",
    nextBilling: "Next billing",
    downgradeScheduled: "Pro until {{date}}, then Free.",
    upgrade: "Upgrade to Pro",
    downgrade: "Cancel subscription",
    undoDowngrade: "Keep Pro",
    downgradeScheduledToast: "Cancellation scheduled — you keep Pro until the end of the period.",
    undoDowngradeToast: "Cancellation reversed — you're still Pro.",
    manageBilling: "Manage billing (invoices, card)",
    pastDue: "Your last payment failed. Update your card in Manage billing to keep Pro.",
    activatingSoon: "Activating soon",
    legal: { title: "LEGAL", terms: "Terms of Service", privacy: "Privacy Policy" },
    support: "SUPPORT",
    contactSupport: "Contact support",

    danger: "DANGER ZONE",
    deleteAccount: "Delete account",
    deleteConfirmTitle: "Delete your account?",
    deleteConfirmBody:
      "This permanently deletes your profile, subscriptions and salary history. This cannot be undone.",
    deleteConfirmAction: "Yes, delete everything",
    deleting: "Deleting…",
    deleted: "Your account has been deleted.",
    deleteFailed: "Could not delete account. Please try again or contact support.",
    dataSection: "YOUR DATA",
    exportCsv: "Export data (CSV)",
    exportReady: "Export ready",
    nothingToExport: "Nothing to export yet",
    exportProOnly: "CSV export is a Pro feature.",
  },

  ribbon: {
    free: "You're on the Free plan · Upgrade to Pro",
  },
  addExtra: {
    upgradeOption: "Upgrade to Pro",
    upgradeBody: "Unlock unlimited subscriptions, full salary history and stats.",
  },
  categories: {
    streaming: "Streaming",
    music: "Music",
    audiobooks: "Audiobooks",
    ai: "AI",
    productivity: "Productivity",
    cloud_storage: "Cloud storage",
    telecom: "Telecom",
    fitness: "Fitness",
    news: "News",
    software: "Software",
    gaming: "Gaming",
    vpn: "VPN",
    transport: "Transport",
    food_delivery: "Food delivery",
    social_media: "Social media",
    communication: "Communication",
    other: "Other",
  },
  cycles: {
    monthly: "Monthly",
    quarterly: "Quarterly",
    yearly: "Yearly",
  },
  countries: {
    NO: "Norway",
    SE: "Sweden",
    DK: "Denmark",
  },
  add: {
    eyebrow: "Add",
    title: "What would you like to add?",
    subOption: "Add a subscription",
    subBody: "Recurring services — streaming, telecom, fitness, more.",
    incomeOption: "Log salary / income",
    incomeBody: "Track salary changes, bonuses and purchasing power.",
    incomeLocked: "Available after you add your first subscription.",
  },
  subsExtra: {
    catalogUpdated: "Catalog price updated",
    updateTo: "Update to {{value}}",
    priceUpdated: "Price updated",
    catalogPriceChangedHelp:
      "The price for this plan in our catalog has changed since you added it. Your saved amount won't change automatically — update it manually if you want to match the new catalog price.",
  },
  salaryExtra: {
    type: "Type",
    typeSalary: "Salary",
    typeRaise: "Raise",
    typeBonus: "Bonus",
    typeOther: "Other",
  },
  admin: {
    title: "Admin",
    eyebrow: "Catalog & inflation",
    tabs: { catalog: "Catalog", inflation: "Inflation" },
    overview: "Overview",
    services: "Services",
    plans: "Plans",
    needsVerify: "Need re-verification",
    backToApp: "Back to app",
    filters: { country: "Country", category: "Category", active: "Active" },
    columns: {
      service: "Service",
      plan: "Plan",
      price: "Price",
      cycle: "Cycle",
      category: "Category",
      verified: "Last verified",
      active: "Active",
      source: "Source",
      actions: "",
    },
    addService: "Add service",
    addPlan: "Add plan",
    fetchApi: "Fetch from API",
    fetchApiBody: "Auto-fetch is not yet configured. Update prices manually above.",
    notes: "Notes",
    saved: "Saved",
    deleted: "Deleted",
    confirmDelete: "Delete this entry?",
    inflation: {
      title: "Annual inflation",
      year: "Year",
      rate: "Rate %",
      source: "Source",
      add: "Add year",
      kind: "Kind",
      kindArsvekst: "Annual average (official)",
      kindTolv: "12-mo. change (provisional)",
      asOfMonth: "Month (1–12)",
      invalidMonth: "Invalid month (1–12)",
      kindHelp:
        'Use "Annual average" for completed years — this is SSB\'s official, final figure, published in January the following year. Use "12-mo. change" only for the current, in-progress year, before its annual average exists. An annual-average row always takes priority over a 12-mo. row for the same year.',
    },
    accessDenied: "You don't have admin access.",
    open: "Open admin",
  },
  pricing: {
    eyebrow: "Pricing",
    headline: "Simple, honest pricing.",
    body: "Start free. Upgrade when VISIKT earns its place in your financial life.",
    trialBadge: "14-day free trial",
    noTrialNote: "No free trial — start with the free tier and upgrade anytime.",
    free: {
      name: "Free",
      price: "0",
      description: "The essentials, forever free.",
      cta: "Get started free",
      features: [
        "Up to 3 subscriptions",
        "Up to 3 years of salary history",
        "Dashboard overview",
        "Norwegian CPI inflation data for current year",
      ],
    },
    pro: {
      name: "Pro",
      priceMonthly: "79",
      priceYearly: "690",
      perMonth: "/ month",
      perYear: "/ year",
      yearlySaving: "Save 27%",
      description: "Full financial clarity, no limits.",
      ctaWithTrial: "Start 14-day free trial",
      ctaNoTrial: "Get Pro",
      features: [
        "Unlimited subscriptions",
        "Full salary history",
        "Stats explorer - metric × range filters",
        "Export data as CSV",
        "Multi-currency subscriptions",
        "Historical Norwegian CPI inflation data ",
      ],
    },
    billingToggle: {
      monthly: "Monthly",
      yearly: "Yearly",
    },
    faq: {
      title: "Common questions",
      q1: "Can I change plans later?",
      a1: "Yes. Upgrade or downgrade at any time.",
      q2: "What happens to my data if I downgrade?",
      a2: "Your data is kept. If you have more than 3 subscriptions or 3 years of salary entries, existing entries are preserved but they will not be visible and you won't be able to add new ones until you upgrade again.",
      q3: "Is my financial data safe?",
      a3: "Yes. All data is stored in accordance with current privacy and GDPR rules, only you can read your data. We never sell or share it.",
      q4: "",
      a4: "",
      stillHave: "Still have questions?",
      contactCta: "Contact support",
    },

    footer: "All prices include VAT where applicable. Billed in NOK.",
  },
  requestAccess: {
    eyebrow: "Early access",
    headline: "Request an invitation.",
    body: "VISIKT is currently invite-only. Fill in the form below and we'll send you an invitation link when your spot is ready.",
    name: "Full name",
    email: "Email address",
    heardAbout: "Where did you hear about VISIKT?",
    heardAboutPlaceholder: "e.g. Twitter, a friend, Product Hunt…",
    submit: "Send request",
    submitting: "Sending…",
    successHeadline: "Request received.",
    successBody: "Thanks — we'll be in touch. Keep an eye on your inbox.",
    backToSignIn: "Back to sign in",
    namePlaceholder: "Alex Smith",
    errors: {
      submitFailed: "Something went wrong submitting your request. Please try again in a bit.",
    },
  },
  about: {
    eyebrow: "About",
    headline: "About VISIKT",
    intro:
      "VISIKT helps you see your financial reality, recurring subscriptions, salary growth, and real (inflation-adjusted) purchasing power in one place. Built Norway-first, using official Norwegian CPI to translate your salary into real terms.",
    whatSectionTitle: "What VISIKT does",
    whatBody:
      "Track every recurring cost, log your salary over time, and see how your purchasing power actually moves once inflation is factored in. Clarity, not budgeting.",
    companySectionTitle: "The company behind VISIKT",
    companyBody:
      "VISIKT is built and operated by VIKEEP AS, a small independent Norwegian company, among other, focused on personal financial clarity.",
    orgNoLabel: "Org.no",
    addressLabel: "Address",
    addressValue: "Oslo, Norway",
    contactLabel: "Contact",
    parentSiteLabel: "Parent company",
    parentSiteValue: "www.vikeep.no",
  },
  privacy: {
    eyebrow: "Privacy",
    headline: "Privacy Policy",
    lastUpdated: "Last updated: 30 June 2026",
    intro:
      'This privacy policy explains how Vikeep AS ("Vikeep", "we", "us", "our") collects and processes personal data when you use VISIKT (the "Service") — our app for tracking subscriptions, salary, and inflation-adjusted purchasing power. We process personal data in accordance with the EU General Data Protection Regulation (Regulation (EU) 2016/679, "GDPR") as incorporated into Norwegian law through the Personal Data Act (personopplysningsloven).',
    tableHeaderPurpose: "Purpose",
    tableHeaderBasis: "Legal basis (GDPR Article 6)",
  },
  terms: {
    eyebrow: "Legal",
    headline: "Terms of Service",
  },
  checkout: {
    confirmationEmailSubject: "Your VISIKT Pro subscription is active",
    confirmationEmailHeadline: "Welcome to VISIKT Pro",
    confirmationEmailIntroMonthly:
      "Thanks for subscribing. Your VISIKT Pro plan is active and will renew monthly at {{price}} NOK until you cancel.",
    confirmationEmailIntroYearly:
      "Thanks for subscribing. Your VISIKT Pro plan is active and will renew yearly at {{price}} NOK until you cancel.",
    confirmationEmailWithdrawal:
      "You expressly requested that we start delivering VISIKT Pro immediately, and confirmed that you lose your 14-day right of withdrawal once the service has been fully delivered. If performance has only partly begun, you may still owe an amount proportionate to what has been supplied.",
    confirmationEmailManage:
      "You can view invoices, downgrade or cancel at any time from your Profile inside the app.",
    confirmationEmailLinks: "Terms: https://visikt.com/terms · Privacy: https://visikt.com/privacy",
    confirmationEmailFooter: "VIKEEP AS · org.nr 938 008 272 · Oslo, Norway · contact@vikeep.no",
  },
} as const;

export default en;
